import { Controller } from "@hotwired/stimulus";

const watchForUrlUpdates = (event) => {
  if (event.target.dataset.updateUrl) {
    history.pushState(
      { turbo_frame_history: true },
      "",
      event.target.dataset.updateUrl
    );
  }
};

const getQueryStringFromUrl = (urlString) => {
  const url = new URL(urlString);

  return url.searchParams.toString();
};

const updateLinkParams = (link, url) => {
  link.originalHref = link.originalHref || link.href;
  const linkUrl = new URL(link.originalHref);
  const frameUrl = new URL(url);

  frameUrl.searchParams.forEach((value, key) => {
    linkUrl.searchParams.append(key, value);
  });
  link.href = linkUrl.toString();
};

const updateFormAction = (form, queryString) => {
  const url = new URL(form.action);
  const newUrl = new URL(form.action);
  newUrl.search = queryString;
  for (const [key, value] of newUrl.searchParams) {
    url.searchParams.set(key, value);
  }
  form.action = url.toString();
};

export default class extends Controller {
  static targets = [
    "itemsFrame",
    "itemsListColumn",
    "items",
    "itemCard",
    "togglerButton",
    "resourceForm",
    "item",
    "filtersLinkWatcher"
  ];

  declare readonly hasItemsFrameTarget: boolean;
  declare readonly itemsFrameTarget: HTMLIFrameElement;

  declare readonly hasItemsListColumTarget: boolean;
  declare readonly itemsListColumnTarget: HTMLElement;

  declare readonly hasItemsTarget: boolean;
  declare readonly itemsTarget: HTMLElement;

  declare readonly hasItemCardTarget: boolean;
  declare readonly itemCardTarget: HTMLElement;

  declare readonly hasTogglerButtonTarget: boolean;
  declare readonly togglerButtonTarget: HTMLElement;

  declare readonly hasResourceFormTarget: boolean;
  declare readonly resourceFormTarget: HTMLFormElement;

  declare readonly hasItemTarget: boolean;
  declare readonly itemTarget: HTMLElement;

  declare readonly hasFiltersLinkWatcherTarget: boolean;
  declare readonly filtersLinkWatcherTarget: HTMLAnchorElement;
  declare readonly filtersLinkWatcherTargets: [HTMLAnchorElement];

  connect() {
    if (this.isCardExpanded()) {
      this.expandCard();
    }
    this.updateItemsHeight();
    this.element.dataset.readyState = "ready";

    document.documentElement.addEventListener(
      "turbo:before-stream-render",
      watchForUrlUpdates
    );

    document.documentElement.addEventListener(
      "turbo:frame-load",
      this.handdleResourceListFrameLoad
    );

    this.updateItemsUrlParams();
  }

  disconnect() {
    if (document) {
      document.documentElement.removeEventListener(
        "turbo:before-stream-render",
        watchForUrlUpdates
      );
      document.documentElement.removeEventListener(
        "turbo:frame-load",
        this.handdleResourceListFrameLoad
      );
    }
  }

  handdleResourceListFrameLoad = ({ target: frame }) => {
    if (frame?.id !== "resource-list") return;

    this.updateItemsUrlParams(frame);
  };

  updateItemsUrlParams(frame: HTMLElement = this.itemsFrameTarget) {
    if (this.hasResourceFormTarget) {
      const queryString = frame.src
        ? getQueryStringFromUrl(frame.src)
        : getQueryStringFromUrl(location.href);
      updateFormAction(this.resourceFormTarget, queryString);
      this.resourceFormTarget
        .querySelectorAll<HTMLInputElement>("input[name=list_query_string]")
        .forEach((input) => {
          input.value = queryString;
        });
    }

    if (this.hasFiltersLinkWatcherTarget) {
      if (frame.src) {
        this.filtersLinkWatcherTargets.forEach((link) => {
          updateLinkParams(link, frame.src);
        });
      }
    }
  }

  filtersLinkWatcherTargetConnected() {
    if (this.hasItemsFrameTarget && this.itemsFrameTarget.src) {
      updateLinkParams(
        this.filtersLinkWatcherTarget,
        this.itemsFrameTarget.src
      );
    }
  }

  resourceFormTargetConnected() {
    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = "list_query_string";

    const frame = this.itemsFrameTarget;
    hiddenInput.value = frame.src ? getQueryStringFromUrl(frame.src) : "";
    this.resourceFormTarget.appendChild(hiddenInput);

    this.expandCard();
  }

  itemsTargetConnected() {
    this.updateItemsHeight();
  }

  itemCardTargetConnected() {
    this.expandCard();
  }

  itemTargetConnected(item) {
    if (item.href === location.href) {
      item.classList.add("active-item");
    }
  }

  toggleCard(e: { preventDefault: () => void }) {
    e.preventDefault();
    if (this.isCardExpanded()) {
      this.collapseCard();
    } else {
      this.expandCard();
    }
  }

  isCardExpanded() {
    return this.element.classList.contains("card-expanded");
  }

  handleItemSelected(e) {
    this.removeCurrentActiveItem();
    this.expandCard();
    e.target.closest("a.items-list-row").classList.add("active-item");
  }

  onNewItem() {
    this.removeCurrentActiveItem();
  }

  removeCurrentActiveItem() {
    this.itemsTarget
      .querySelectorAll(".items-list-row.active-item")
      .forEach((item) => {
        item.classList.remove("active-item");
      });
  }

  expandCard() {
    this.element.classList.add("card-expanded");
    if (this.hasItemCardTarget) {
      this.showItemsListColum();
      this.itemCardTarget.classList.add("grow");
      this.itemsListColumnTarget.classList.remove("grow");
      this.togglerButtonTarget
        .querySelector("span.la")
        ?.classList.replace("la-chevron-left", "la-chevron-right");
    } else {
      this.hideItemsListColum();
    }
  }

  collapseCard() {
    this.element.classList.remove("card-expanded");
    this.itemCardTarget.classList.remove("grow");
    this.itemsListColumnTarget.classList.add("grow");
    this.togglerButtonTarget
      .querySelector("span.la")
      ?.classList.replace("la-chevron-right", "la-chevron-left");
  }

  updateItemsHeight() {
    const height =
      this.itemsListColumnTarget.clientHeight - this.getItemsListTopOffset();

    this.itemsTarget.style.height = `${height}px`;
  }

  getItemsListTopOffset() {
    return this.itemsTarget.offsetTop;
  }

  hideItemsListColum() {
    this.itemsListColumnTarget.classList.add("hidden");
  }

  showItemsListColum() {
    this.itemsListColumnTarget.classList.remove("hidden");
  }
}
